import { Fragment, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import logger from 'logger';
import { useRouter } from 'next/router';
import PasswordValidation from 'src/components/SignupAndLogin/PasswordValidation';
import { requestPasswordReset } from 'src/services/auth-service/forgot-password.service';
import { resetPassword } from 'src/services/auth-service/reset-password.service';
import { RegexPatterns } from 'src/utils/constants';
import { object, ref, string } from 'yup';

import MatchingPasswordValidation from './MatchingPasswordValidation';

const Form: React.FC<FormProps> = ({ isForgotPassword }) => {
  return isForgotPassword ? <ForgotPasswordForm /> : <ResetPasswordForm />;
};
const ForgotPasswordForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const router = useRouter();
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
    mode: 'onChange',
  });
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );
  const onSubmit = async () => {
    setIsLoading(true);
    const values = getValues();
    // Rails always returns success regardless of whether the email matches
    // (anti-enumeration), so we always redirect to /login. A logged error from
    // the service indicates something unexpected (e.g. tenant misconfig); we
    // still redirect so users don't see a different UX based on the lookup.
    const result = await requestPasswordReset({ email: values.email });
    setIsLoading(false);
    if (!result.ok) {
      logger.error({ msg: 'ForgotPasswordSubmitFailed' });
    }
    router.push({
      pathname: '/login',
      query: { comesFrom: 'forgot-password' },
    });
  };

  return (
    <FormControl fullWidth error={!isValid}>
      <Grid
        container
        spacing={4}
        columns={12}
        sx={{
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Grid sx={{ marginTop: '0.5rem' }}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <TextField
                color="primary"
                sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
                placeholder="kale@example.com"
                label="Email"
                required
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Grid>
        <Grid sx={{ marginTop: '1rem' }}>
          <Button
            type="submit"
            size="large"
            variant={isLoading ? 'outlined' : 'contained'}
            disabled={!isValid || isLoading}
            sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
            onClick={handleSubmit(onSubmit)}
          >
            {isLoading && (
              <CircularProgress
                sx={{ marginRight: '0.25rem', color: theme.palette.grey[300] }}
                size={20}
              />
            )}
            SUBMIT
          </Button>
        </Grid>
      </Grid>
    </FormControl>
  );
};
const ResetPasswordForm: React.FC = () => {
  const router = useRouter();
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );
  const [password, setPassword] = useState<string>('');
  const [isPasswordShown, setIsPasswordShown] = useState<boolean>(false);
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [isPasswordConfShown, setIsPasswordConfShown] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
    setError,
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  const onSubmit = async () => {
    setIsLoading(true);
    const values = getValues();
    try {
      const resetToken = router.query.reset_password_token;
      if (!resetToken || typeof resetToken !== 'string') {
        throw new Error(
          'Attempted to reset password with no reset token present in query params',
        );
      }
      const result = await resetPassword({
        resetPasswordToken: resetToken,
        password: values.password as string,
        passwordConfirmation: values.passwordConfirmation as string,
      });

      if (result.ok) {
        router.push({
          pathname: '/login',
          query: { comesFrom: 'reset-password' },
        });
      } else if (result.reason === 'validation') {
        const { fieldErrors } = result;
        // Only password / passwordConfirmation map to fields the user can fix
        // here; a resetPasswordToken error means the link is invalid or
        // expired and there's no field to surface it on. @todo: redirect to
        // /forgot-password in that case so the user can request a fresh link.
        if (fieldErrors.password) {
          setError('password', {
            type: 'custom',
            message: fieldErrors.password,
          });
        }
        if (fieldErrors.passwordConfirmation) {
          setError('passwordConfirmation', {
            type: 'custom',
            message: fieldErrors.passwordConfirmation,
          });
        }
      }
    } catch (err: any) {
      logger.error({
        msg: 'ResetPasswordUnexpectedError',
        noticable: err,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormControl fullWidth error={!isValid}>
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Fragment>
          <Grid sx={{ marginTop: '0.5rem' }}>
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field: { onChange }, formState: { errors } }) => (
                <TextField
                  color="primary"
                  sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
                  label="New password"
                  required
                  value={password}
                  type={isPasswordShown ? 'text' : 'password'}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    onChange(e.target.value);
                  }}
                  error={!!errors?.password}
                  helperText={
                    errors?.password?.type === 'custom'
                      ? errors?.password?.message
                      : null
                  }
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setIsPasswordShown(!isPasswordShown)}
                            edge="end"
                          >
                            {isPasswordShown ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
            <Box sx={{ marginTop: '0.5rem' }}>
              <PasswordValidation password={password} />
            </Box>
          </Grid>
          <Grid sx={{ marginTop: '0.5rem' }}>
            <Controller
              name="passwordConfirmation"
              control={control}
              defaultValue=""
              render={({ field: { onChange } }) => (
                <TextField
                  color="primary"
                  sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
                  label="Confirm new password"
                  required
                  value={passwordConfirmation}
                  type={isPasswordConfShown ? 'text' : 'password'}
                  onChange={(e) => {
                    setPasswordConfirmation(e.target.value);
                    onChange(e.target.value);
                  }}
                  error={
                    !!password &&
                    !!passwordConfirmation &&
                    password !== passwordConfirmation
                  }
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setIsPasswordConfShown(!isPasswordConfShown)
                            }
                            edge="end"
                          >
                            {isPasswordConfShown ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid>
          {passwordConfirmation && (
            <Grid
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                marginTop: '-0.5rem',
              }}
            >
              <MatchingPasswordValidation
                password={password}
                passwordConfirmation={passwordConfirmation}
              />
            </Grid>
          )}
        </Fragment>
        <Grid>
          <Button
            type="submit"
            size="large"
            variant={isLoading ? 'outlined' : 'contained'}
            disabled={!isValid || isLoading}
            sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
            onClick={handleSubmit(onSubmit)}
          >
            {isLoading && (
              <CircularProgress
                sx={{ marginRight: '0.25rem', color: theme.palette.grey[300] }}
                size={20}
              />
            )}
            CHANGE MY PASSWORD
          </Button>
        </Grid>
      </Grid>
    </FormControl>
  );
};

export default Form;

const largeInputStyles = {
  width: '30rem',
};

const mobileInputStyles = {
  width: '20rem',
};

interface FormProps {
  isForgotPassword?: boolean | null;
}

const forgotPasswordSchema = object({
  email: string().required().email().defined(),
});

const resetPasswordSchema = object({
  password: string()
    .required(' ')
    .matches(RegexPatterns.IS_BIGGER_THAN_SIX_CHARS, ' ')
    .matches(RegexPatterns.HAS_UPPERCASE_LETTER, ' ')
    .matches(RegexPatterns.HAS_NUMBER, ' ')
    .matches(RegexPatterns.HAS_SPECIAL_CHAR, ' ')
    .defined(),
  passwordConfirmation: string()
    .required()
    .oneOf([ref('password')]),
});
